/**
 * Locale-aware formatters (doc 00 §10).
 * Currency = MKD (денар). Money is stored/transported as integer minor units
 * (денари = MKD × 100); we divide by 100 here at the display edge only.
 */

export type SupportedLocale = 'mk' | 'sq' | 'en';

const BCP47: Record<SupportedLocale, string> = {
  mk: 'mk-MK',
  sq: 'sq-AL',
  en: 'en-US',
};

const MINOR_UNITS_PER_MAJOR = 100;

/**
 * Format an integer minor-unit (денари) amount as a localized MKD string.
 * e.g. formatMKD(123450, 'mk') -> "1.234,50 ден" (locale-dependent rendering).
 */
export function formatMKD(minorUnits: number, locale: SupportedLocale = 'mk'): string {
  if (!Number.isFinite(minorUnits)) {
    throw new RangeError(`formatMKD expects a finite number, got ${String(minorUnits)}`);
  }
  const major = minorUnits / MINOR_UNITS_PER_MAJOR;
  return new Intl.NumberFormat(BCP47[locale], {
    style: 'currency',
    currency: 'MKD',
  }).format(major);
}

/** Format a plain number per locale. */
export function formatNumber(
  value: number,
  locale: SupportedLocale = 'mk',
  options?: Intl.NumberFormatOptions,
): string {
  return new Intl.NumberFormat(BCP47[locale], options).format(value);
}

/** Format a Date (or ISO string / epoch ms) per locale. */
export function formatDate(
  value: Date | string | number,
  locale: SupportedLocale = 'mk',
  options: Intl.DateTimeFormatOptions = { dateStyle: 'medium' },
): string {
  const date = value instanceof Date ? value : new Date(value);
  return new Intl.DateTimeFormat(BCP47[locale], options).format(date);
}
